import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { type SubmitEvent, useState } from 'react'

import type { PlatformUserSummary } from '@/bindings/PlatformUserSummary'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { platformUserDetailQuery, platformUserKeys, platformUserName, platformUserPath } from '@/features/platform-users/api'
import { FirstLoginHint } from '@/features/platform-users/first-login-hint'
import { draftFromUser, isEmailError, toUserRequest } from '@/features/platform-users/user-draft'
import { UserFields } from '@/features/platform-users/user-fields'
import { request } from '@/lib/api'
import { updateAuth, useAuth } from '@/lib/auth-store'
import { useT } from '@/lib/i18n/translate'

/** Admin edit surface (E4): names, email, admin flag; delete; the first-login relay. */
export function PlatformUserDetailPage() {
  const t = useT()
  const { userId } = useParams({ from: '/platform-auth/users/$userId' })
  const detail = useQuery(platformUserDetailQuery(userId))

  // Data wins over a failed background refetch; only a first load with nothing shows the error.
  const user = detail.data
  if (!user) {
    return (
      <Page width="sm" className="flex flex-col gap-4">
        {detail.error ? <FormError error={detail.error} /> : <p className="text-muted text-sm">{t('common.loading')}</p>}
        {detail.error && <BackLink />}
      </Page>
    )
  }

  return (
    <Page width="sm" className="flex flex-col gap-4">
      <BackLink />
      <EditCard user={user} />
      <Card className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">{t('users.firstLoginTitle')}</h2>
        <FirstLoginHint email={user.email} />
      </Card>
      <DeleteCard user={user} />
    </Page>
  )
}

function BackLink() {
  const t = useT()
  return (
    <Link to="/users" search={{ page: 1 }} className="text-accent-ink self-start text-sm font-medium hover:underline">
      ← {t('users.title')}
    </Link>
  )
}

function EditCard({ user }: { user: PlatformUserSummary }) {
  const t = useT()
  const queryClient = useQueryClient()
  const auth = useAuth('platform')
  const self = auth.status === 'authenticated' && auth.user.id === user.id
  const [draft, setDraft] = useState(() => draftFromUser(user))

  // A refetch that changed the row refreshes the form; otherwise Save would PUT stale values back.
  const [seen, setSeen] = useState(user)
  if (seen !== user) {
    setSeen(user)
    setDraft(draftFromUser(user))
  }

  const save = useMutation({
    mutationFn: () => request<PlatformUserSummary>(platformUserPath(user.id), { method: 'PUT', body: toUserRequest(draft) }),
    onSuccess: (saved) => {
      queryClient.setQueryData(platformUserKeys.detail(saved.id), saved)
      // Editing yourself: the nav's admin filter and the avatar follow at once.
      if (self && auth.status === 'authenticated') {
        updateAuth('platform', {
          user: { ...auth.user, email: saved.email, first_name: saved.first_name, last_name: saved.last_name, is_admin: saved.is_admin },
        })
      }
      return queryClient.invalidateQueries({ queryKey: platformUserKeys.all })
    },
  })

  function submit(event: SubmitEvent) {
    event.preventDefault()
    save.mutate()
  }

  const emailError = isEmailError(save.error) ? save.error : undefined

  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <h1 className="truncate text-xl font-semibold">{platformUserName(user)}</h1>
        <span className="flex shrink-0 gap-2">
          {user.is_admin && <Badge tone="success">{t('users.admin')}</Badge>}
          {self && <Badge tone="muted">{t('users.you')}</Badge>}
        </span>
      </div>
      <p className="text-muted text-sm">
        {t('users.createdAt')}: {new Date(user.created_at).toLocaleDateString()}
      </p>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <UserFields
          value={draft}
          onChange={(next) => {
            setDraft(next)
            if (save.error) save.reset()
          }}
          emailError={emailError}
        />
        {save.isSuccess && (
          <p role="status" className="text-muted text-sm">
            {t('profile.saved')}
          </p>
        )}
        <FormError error={emailError ? undefined : save.error} />
        <Button type="submit" loading={save.isPending} className="self-start">
          {t('common.save')}
        </Button>
      </form>
    </Card>
  )
}

/** Delete needs a second press (PRD: keep destructive actions apart); self goes via profile. */
function DeleteCard({ user }: { user: PlatformUserSummary }) {
  const t = useT()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const auth = useAuth('platform')
  const self = auth.status === 'authenticated' && auth.user.id === user.id

  const remove = useMutation({
    mutationFn: () => request<void>(platformUserPath(user.id), { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: platformUserKeys.all })
      await navigate({ to: '/users', search: { page: 1 }, replace: true })
    },
  })

  return (
    <Card className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">{t('users.deleteTitle')}</h2>
      <p className="text-muted text-sm">{t(self ? 'users.deleteSelfHint' : 'users.deleteIntro')}</p>
      {!self && (
        <>
          <FormError error={remove.error} />
          <ConfirmButton
            label={t('users.delete')}
            confirmLabel={t('users.confirmDelete')}
            confirmAriaLabel={t('users.confirmDeleteOf', { name: platformUserName(user) })}
            confirm={() => remove.mutateAsync()}
          />
        </>
      )}
    </Card>
  )
}
